
import { useToast } from "@/hooks/use-toast";
import { useMoveActionsMutation } from "@/store/moves/apis";
import { Move } from "@/types";
import {
  ClockIcon,
  CopyCheckIcon,
  CreditCardIcon,
  DollarSignIcon,
  LockOpenIcon,
  MailIcon,
  PhoneIcon,
  SendIcon,
  TrashIcon,
  XIcon,
  CalendarIcon,
} from "lucide-react";
import { Sheet, SheetTrigger, SheetContent } from "@/components/ui/sheet";
import { ConversationMessages } from "@/components/ConversationMessages";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Clone from "./Clone";
import FUReminder from "./FUReminder";
import LogCall from "./LogCall";
import { ChatBubbleIcon } from "@radix-ui/react-icons";
import { SendPaymentLink } from "./SendPaymentLink";
import { RunCharge } from "./RunCharge";
import { getSelectedMover } from "@/store/mover/slice";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import {
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
  DialogDescription,
} from "@/components/ui/dialog";
import { Dialog } from "@/components/ui/dialog";
import MoveInvoice from "../MoveInvoice";
import { isAfter, isEqual, parseISO } from "date-fns";
import SetLeadLost from "./SetLeadLost";
import CancelMove from "./CancelMove";
import ScheduleOnsite from "./ScheduleOnsite";
import { isBooked, isLead } from "@/pages/EditMove/functions";

const Actions = ({
  move,
  refetchMove,
}: {
  move: Move;
  refetchMove: () => void;
}) => {
  const navigate = useNavigate();
  const mover = useSelector(getSelectedMover);
  const [moveActions] = useMoveActionsMutation();
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [onsiteSheetOpen, setOnsiteSheetOpen] = useState(false);
  const { toast } = useToast();

  const [isFUOpen, setIsFUOpen] = useState(false);

  const handleActions = async (action: string) => {
    try {
      await moveActions({
        id: move.id,
        action,
      }).unwrap();

      let message = "";
      switch (action) {
        case "send_estimate":
          message = "Estimate sent";
          break;
        case "send_booking_confirmation":
          message = "Confirmation sent";
          break;
        case "set_to_lost":
          message = "Move marked as lost";
          break;
        case "set_to_completed":
          message = "Move marked as completed";
          break;
        case "reopen_move":
          message = "Move re-opened";
          break;
        case "send_invoice":
          message = "Invoice sent";
          break;
        case "send_receipt":
          message = "Receipt sent";
          break;
      }

      toast({
        title: "Success",
        description: message,
      });

      refetchMove();
    } catch (err) {
      console.log("err", err);
    }
  };



  if (!mover) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col gap-1">
        {move.status !== "completed" && (
          <div
            className="flex items-center space-x-2 py-1 cursor-pointer text-yellow-600 hover:underline hover:underline-offset-4 hover:decoration-yellow-600"
            onClick={() => {
              navigate(
                mover.business_type === "mover"
                  ? `/move/update/${move.id}`
                  : `/job/update/${move.id}`
              );
            }}
          >
            <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
            <span className="text-base font-medium">Update Details</span>
          </div>
        )}

        <Sheet>
          <SheetTrigger asChild>
            <div className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
              <ChatBubbleIcon className="w-4 h-4" />
              <span className="text-base font-medium">Send text/email</span>
            </div>
          </SheetTrigger>
          <SheetContent
            className={cn(
              "w-[90vw] max-w-[90vw]",
              "sm:max-w-[90vw]",
              "lg:max-w-[50vw]"
            )}
          >
            <ConversationMessages
              messages={move.messages}
              customer={move.customer}
              moveId={move.id}
              onMessageSent={refetchMove}
              showHeader={false}
            />
          </SheetContent>
        </Sheet>

        {mover.mover_configuration.enabled_for_payments && (
          <>
            <Sheet>
              <SheetTrigger asChild>
                <div className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                  <SendIcon className="w-4 h-4" />
                  <span className="text-base font-medium">
                    Send Payment Link
                  </span>
                </div>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-screen sm:max-w-[100vw] lg:max-w-[50vw] max-h-[90vh] overflow-y-auto"
              >
                <SendPaymentLink
                  initialMove={move}
                  onSuccess={() => {
                    refetchMove();
                    toast({
                      title: "Success",
                      description: "Payment link sent",
                    });
                  }}
                />
              </SheetContent>
            </Sheet>
            <Sheet>
              <SheetTrigger asChild>
                <div className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                  <CreditCardIcon className="w-4 h-4" />
                  <span className="text-base font-medium">
                    Process a charge
                  </span>
                </div>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-full lg:max-w-[500px] overflow-y-auto"
              >
                <RunCharge
                  initialMove={move}
                  refetchMove={refetchMove}
                  onSuccess={() => {
                    toast({
                      title: "Success",
                      description: "Charge processed successfully",
                    });
                  }}
                />
              </SheetContent>
            </Sheet>
          </>
        )}
        {(move.status === "initial" ||
          move.status === "contacting" ||
          move.status === "quoted") && (
          <>
            <LogCall
              move={move}
              triggerChildren={
                <div className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                  <PhoneIcon className="w-4 h-4" />{" "}
                  <span className="text-base font-medium">Log call</span>
                </div>
              }
              refetchMove={refetchMove}
              setFUOpen={setIsFUOpen}
            />

            <div
              className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500"
              onClick={() => setOnsiteSheetOpen(true)}
            >
              <CalendarIcon className="w-4 h-4" />
              <span className="text-base font-medium">
                {move.upcoming_onsite_event
                  ? "Re-schedule Onsite"
                  : "Schedule Onsite"}
              </span>
            </div>

            <FUReminder
              move={move}
              manualHandlers={{
                onOpenChange: setIsFUOpen,
                open: isFUOpen,
              }}
              triggerChildren={
                <div className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                  <ClockIcon className="w-4 h-4" />
                  <span className="text-base font-medium">
                    Set a follow-up reminder
                  </span>
                </div>
              }
              refetchMove={refetchMove}
            />

            <div
              className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500"
              onClick={() => {
                handleActions("send_estimate");
              }}
            >
              <MailIcon className="w-4 h-4" />
              <span className="text-base font-medium">
                Send {move.status === "quoted" && "revised"} estimate
              </span>
            </div>
          </>
        )}
        {move.status === "completed" &&
          move.invoice?.total &&
          move.invoice?.total > 0 &&
          !move.invoice.paid_in_full && (
            <div
              className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500"
              onClick={() => {
                handleActions("send_invoice");
              }}
            >
              <MailIcon className="w-4 h-4" />
              <span className="text-base font-medium">
                Send invoice to customer
              </span>
            </div>
          )}
        {isBooked(move) &&
          (isEqual(new Date(), parseISO(move.service_date)) ||
            isAfter(new Date(), parseISO(move.service_date))) && (
            <Dialog
              open={finalizeDialogOpen}
              onOpenChange={setFinalizeDialogOpen}
            >
              <DialogTrigger asChild>
                <div className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                  <DollarSignIcon className="w-4 h-4" />
                  <span className="text-base font-medium">
                    {move.invoice ? "Update Invoice" : "Finalize Job"}
                  </span>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Finalize Job</DialogTitle>
                  <DialogDescription>
                    Enter the final amounts for the job.
                  </DialogDescription>
                </DialogHeader>
                <MoveInvoice
                  move={move}
                  onSuccess={() => {
                    refetchMove();
                    setFinalizeDialogOpen(false);
                  }}
                  onCancel={() => {
                    setFinalizeDialogOpen(false);
                  }}
                />
              </DialogContent>
            </Dialog>
          )}

        {move.status === "booked" && (
          <div
            className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500"
            onClick={() => {
              handleActions("send_confirmation");
            }}
          >
            <MailIcon className="w-4 h-4" />
            <span className="text-base font-medium">
              {move.status === "booked" ? "Resend" : "Send"} confirmation
            </span>
          </div>
        )}
        <div>
          <Clone
            move={move}
            triggerChildren={
              <div className="flex items-center space-x-2 py-1 cursor-pointer hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                <CopyCheckIcon className="w-4 h-4" />
                <span className="text-base font-medium">Clone this move</span>
              </div>
            }
          />
        </div>
        {isBooked(move) && (
          <CancelMove
            move={move}
            triggerChildren={
              <div className="flex items-center space-x-2 py-1 cursor-pointer text-red-600 font-semibold hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                <XIcon className="w-4 h-4" />
                <span className="text-base font-medium">Cancel move</span>
              </div>
            }
            refetchMove={refetchMove}
          />
        )}
        {isLead(move) && (
          <SetLeadLost
            move={move}
            triggerChildren={
              <div className="flex items-center space-x-2 py-1 cursor-pointer text-red-600 font-semibold hover:underline hover:underline-offset-4 hover:decoration-yellow-500">
                <TrashIcon className="w-4 h-4" />
                <span className="text-base font-medium">Set lead to Lost</span>
              </div>
            }
            refetchMove={refetchMove}
          />
        )}
        {(move.status === "lost" || move.status === "canceled") && (
          <div
            className="flex items-center space-x-2 py-1 cursor-pointer font-semibold hover:underline hover:underline-offset-4 hover:decoration-yellow-500"
            onClick={() => {
              handleActions("reopen_move");
            }}
          >
            <LockOpenIcon className="w-4 h-4" />
            <span className="text-base font-medium">
              Reopen this {move.status === "lost" ? "lead" : "move"}
            </span>
          </div>
        )}
      </div>
      <ScheduleOnsite
        move={move}
        open={onsiteSheetOpen}
        onOpenChange={setOnsiteSheetOpen}
        onSuccess={refetchMove}
      />
    </>
  );
};

export default Actions;
